/**
 * Business-area multi-select (owner request 2026-09-21: one client can belong to several businesses).
 * Checkbox chips; used everywhere a person is added, edited or imported. Saving sends `verticalIds`.
 */
import { useMemo } from 'react';
import { Checkbox } from './checkbox.js';
import { cn } from '../../lib/utils.js';
import { useSession } from '../../lib/session.js';
import { areaLabel } from '../../lib/plain.js';

export interface AreaOption { value: string; label: string; key?: string }

/** Pure default, exported for tests: the top-bar area when one is chosen, else the only area, else none. */
export function defaultAreaIds(switcher: string, options: { value: string; key?: string }[]): string[] {
  if (options.length === 1) return [options[0]!.value];
  const hit = switcher !== 'all' ? options.find((o) => o.key === switcher) : undefined;
  return hit ? [hit.value] : [];
}

/** The user's areas as options, plus the default ticks. */
export function useAreaOptions(): { options: AreaOption[]; defaults: string[] } {
  const { me, vertical } = useSession();
  const options = useMemo(() => (me?.memberships ?? []).map((m) => ({ value: m.verticalId, label: areaLabel(m.verticalKey), key: m.verticalKey })), [me]);
  const defaults = useMemo(() => defaultAreaIds(vertical, options), [vertical, options]);
  return { options, defaults };
}

interface Props {
  options: AreaOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  disabled?: boolean;
  className?: string;
}

export function AreaMultiSelect({ options, value, onChange, id, disabled, className, ...aria }: Props) {
  const toggle = (v: string, on: boolean) => {
    const next = on ? [...value.filter((x) => x !== v), v] : value.filter((x) => x !== v);
    // Keep the order of the options so the first ticked area is predictable.
    onChange(options.map((o) => o.value).filter((x) => next.includes(x)));
  };
  return (
    <div id={id} role="group" aria-label={aria['aria-label'] ?? 'Business areas'} aria-describedby={aria['aria-describedby']} className={cn('flex flex-wrap gap-2', className)}>
      {options.map((o) => {
        const on = value.includes(o.value);
        const cid = `${id ?? 'area'}-${o.value}`;
        return (
          <label key={o.value} htmlFor={cid}
            className={cn('inline-flex cursor-pointer select-none items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
              on ? 'border-primary bg-primary/10 text-foreground' : 'bg-card text-muted-foreground hover:bg-muted', disabled && 'cursor-not-allowed opacity-60')}>
            <Checkbox id={cid} checked={on} disabled={disabled} onCheckedChange={(c) => toggle(o.value, c === true)} aria-label={o.label} />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

/** Plain summary of what the server skipped ("Already in Pharma ..."), or '' when nothing was. */
export function skippedText(skipped: { reason: string }[] | undefined): string {
  return (skipped ?? []).map((s) => s.reason).join(' ');
}

/**
 * CSV "areas" (or "area") cell -> area ids. Several values separated by ";" or "|" (a comma splits CSV
 * columns, so it is not used). Each value is matched to an area name or key, ignoring case.
 */
export function areasFromCell(cell: string, options: { value: string; label: string; key?: string }[]): { ids: string[]; unknown: string[] } {
  const ids: string[] = [];
  const unknown: string[] = [];
  for (const raw of cell.split(/[;|]/)) {
    const t = raw.trim().toLowerCase();
    if (!t) continue;
    const hit = options.find((o) => o.label.toLowerCase() === t || o.key?.toLowerCase() === t);
    if (!hit) unknown.push(raw.trim());
    else if (!ids.includes(hit.value)) ids.push(hit.value);
  }
  return { ids, unknown };
}
